import { PERIOD_BAR_STATE } from "@components/PeriodBar/periodBarUtils";
import { resolvePayPlanPeriodState } from "@components/PayPlanRowCard/payPlanRowCardUtils";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";

export function localTodayIso(now = new Date()) {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export function formatVersionAmountLabel(version, currencyCode = "USD") {
  return `${currencyCode} ${formatPayRangeAmount(version?.minAmount)} – ${formatPayRangeAmount(
    version?.maxAmount
  )}`;
}

/** Period state comes from the dates and today, never from the API. Voided has none. */
export function resolveVersionPeriodState(version, today) {
  if (!version || version.status === "voided") return "";
  return resolvePayPlanPeriodState(version.effectiveFrom, version.effectiveTo ?? null, today);
}

/** Timeline-bar colour: a draft is magenta, otherwise it follows the period state. */
export function versionToBarState(version, today) {
  if (version.status === "draft") return PERIOD_BAR_STATE.DRAFT;
  return resolveVersionPeriodState(version, today) || PERIOD_BAR_STATE.LAPSED;
}

/** Flatten every pay range's versions, tagging each with its range and a unique id. */
export function flattenRangeVersions(payRanges) {
  return (payRanges || []).flatMap((range) =>
    (range.versions || []).map((version) => ({
      ...version,
      uid: `${range.id}:${version.id}`,
      rangeId: range.id,
      rangeCode: range.code || "",
      currencyCode: range.currencyCode || "USD",
    }))
  );
}

function startTime(version) {
  const time = version.effectiveFrom ? new Date(version.effectiveFrom).getTime() : NaN;
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
}

/** Newest first, so Future sits on top. Voided versions follow the live ones. */
export function sortVersionsNewestFirst(versions) {
  const byNewest = (a, b) => startTime(b) - startTime(a);
  const live = versions.filter((v) => v.status !== "voided").sort(byNewest);
  const voided = versions.filter((v) => v.status === "voided").sort(byNewest);
  return [...live, ...voided];
}

export function buildTimelineLanes(payRanges, today) {
  return (payRanges || []).map((range) => ({
    key: `range-${range.id}`,
    label: "Versions",
    sublabel: range.code || "",
    emptyText: "No versions",
    records: (range.versions || [])
      .filter((version) => version.status !== "voided")
      .map((version) => ({
        id: `${range.id}:${version.id}`,
        label: formatVersionAmountLabel(version, range.currencyCode || "USD"),
        state: versionToBarState(version, today),
        effectiveFrom: version.effectiveFrom,
        effectiveTo: version.effectiveTo ?? null,
        meta: version.status === "draft" ? { statusLabel: "Draft" } : undefined,
      })),
  }));
}
